import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../App';
import { Appointment, UserProfile, Medication } from '../types';
import { Calendar, Clock, User, FileText, Plus, Video, Clipboard, Package, ArrowRight, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';

export default function Dashboard() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, waiting: 0, completed: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    let q;
    if (user.role === 'patient') {
      q = query(
        collection(db, 'appointments'),
        where('patientId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
    } else if (user.role === 'doctor') {
      q = query(
        collection(db, 'appointments'),
        where('doctorId', '==', user.uid),
        where('status', 'in', ['triaged', 'in-progress']),
        orderBy('createdAt', 'asc')
      );
    } else {
      q = query(
        collection(db, 'appointments'),
        where('status', 'in', ['waiting', 'triaged']),
        orderBy('createdAt', 'asc')
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Appointment));
      setAppointments(docs);
      
      // Simple stats
      setStats({
        total: docs.length,
        waiting: docs.filter(a => a.status === 'waiting').length,
        completed: docs.filter(a => a.status === 'completed').length
      });
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const startConsultation = async () => {
    if (!user) return;
    try {
      const docRef = await addDoc(collection(db, 'appointments'), {
        patientId: user.uid,
        status: 'waiting',
        createdAt: new Date().toISOString(),
        specialty: 'Clínico Geral' // Default
      });
      // In a real app, we'd notify attendants here
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-text-main font-display">Olá, {user?.name}</h1>
        <p className="text-text-muted">Bem-vindo ao seu painel de telemedicina.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-text-muted/10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
            <span className="text-2xl font-bold text-text-main">{stats.total}</span>
          </div>
          <h3 className="text-text-muted font-medium">Total de Atendimentos</h3>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-text-muted/10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <Clock className="w-6 h-6 text-secondary-dark" />
            </div>
            <span className="text-2xl font-bold text-text-main">{stats.waiting}</span>
          </div>
          <h3 className="text-text-muted font-bold">Em Espera / Triagem</h3>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-text-muted/10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-2xl font-bold text-text-main">{stats.completed}</span>
          </div>
          <h3 className="text-text-muted font-bold">Concluídos</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <section className="bg-white rounded-xl shadow-sm border border-text-muted/10 overflow-hidden">
            <div className="px-6 py-4 border-b border-text-muted/10 flex justify-between items-center">
              <h2 className="text-lg font-bold text-text-main font-display">Atendimentos Recentes</h2>
              {user?.role === 'patient' && (
                <button
                  onClick={startConsultation}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" /> Nova Consulta
                </button>
              )}
            </div>
            <div className="divide-y divide-text-muted/10">
              {appointments.length === 0 ? (
                <div className="px-6 py-12 text-center text-text-muted">
                  Nenhum atendimento encontrado.
                </div>
              ) : (
                appointments.map((appointment) => (
                  <div key={appointment.id} className="px-6 py-4 flex items-center justify-between hover:bg-bg-light transition-colors">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                        appointment.status === 'completed' ? 'bg-primary/20 text-primary' :
                        appointment.status === 'waiting' ? 'bg-secondary/20 text-secondary-dark' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {appointment.status === 'completed' ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="font-bold text-text-main">{appointment.specialty || 'Clínico Geral'}</p>
                        <p className="text-sm text-text-muted">{new Date(appointment.createdAt).toLocaleString('pt-BR')}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                        appointment.status === 'completed' ? 'bg-primary/20 text-primary' :
                        appointment.status === 'waiting' ? 'bg-secondary/20 text-secondary-dark' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {appointment.status === 'waiting' ? 'Na Fila' :
                         appointment.status === 'triaged' ? 'Triado' :
                         appointment.status === 'in-progress' ? 'Em Chamada' : 'Concluído'}
                      </span>
                      {appointment.status === 'in-progress' && (
                        <Link
                          to={`/consultation/${appointment.id}`}
                          className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                        >
                          <Video className="w-4 h-4" />
                        </Link>
                      )}
                      {appointment.prescriptionId && (
                        <Link
                          to={`/prescription/${appointment.prescriptionId}`}
                          className="p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                        >
                          <FileText className="w-4 h-4" />
                        </Link>
                      )}
                      <Link to={appointment.status === 'in-progress' ? `/consultation/${appointment.id}` : appointment.prescriptionId ? `/prescription/${appointment.prescriptionId}` : `/dashboard`} className="text-text-muted hover:text-text-main">
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <div className="space-y-8">
          {/* Quick Actions */}
          <section className="bg-white rounded-xl shadow-sm border border-text-muted/10 p-6">
            <h2 className="text-lg font-bold text-text-main mb-4 font-display">Ações Rápidas</h2>
            <div className="space-y-3">
              {user?.role === 'patient' && (
                <>
                  <Link to="/profile" className="flex items-center p-3 rounded-lg hover:bg-bg-light border border-text-muted/10 transition-colors">
                    <User className="w-5 h-5 text-secondary mr-3" />
                    <span className="text-sm font-bold text-text-main">Completar Perfil</span>
                  </Link>
                  <a href="#" className="flex items-center p-3 rounded-lg hover:bg-bg-light border border-text-muted/10 transition-colors">
                    <Package className="w-5 h-5 text-primary mr-3" />
                    <span className="text-sm font-medium text-text-main">Comprar Medicamentos</span>
                  </a>
                </>
              )}
              {(user?.role === 'attendant' || user?.role === 'admin') && (
                <>
                  <Link to="/triage" className="flex items-center p-3 rounded-lg hover:bg-bg-light border border-text-muted/10 transition-colors">
                    <Clipboard className="w-5 h-5 text-secondary mr-3" />
                    <span className="text-sm font-bold text-text-main">Realizar Triagem</span>
                  </Link>
                  <Link to="/inventory" className="flex items-center p-3 rounded-lg hover:bg-bg-light border border-text-muted/10 transition-colors">
                    <Package className="w-5 h-5 text-primary mr-3" />
                    <span className="text-sm font-medium text-text-main">Gerenciar Estoque</span>
                  </Link>
                </>
              )}
            </div>
          </section>

          {/* Notifications / Alerts */}
          <section className="bg-white rounded-xl shadow-sm border border-text-muted/10 p-6">
            <h2 className="text-lg font-bold text-text-main mb-4 font-display">Notificações</h2>
            <div className="space-y-4">
              {user?.role === 'doctor' && appointments.some(a => a.status === 'triaged') && (
                <div className="flex items-start p-3 bg-secondary/10 rounded-lg border border-secondary/20">
                  <AlertCircle className="w-5 h-5 text-secondary-dark mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-secondary-dark">Novo Paciente na Fila</p>
                    <p className="text-xs text-secondary-dark/70">Há pacientes triados aguardando atendimento.</p>
                  </div>
                </div>
              )}
              <div className="text-sm text-text-muted italic">
                Nenhuma outra notificação no momento.
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function CheckCircle2(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
